// src/components/ui/ColorPicker/ColorPicker.tsx
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import { checkmark, addOutline, closeCircle } from 'ionicons/icons';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface ColorPickerProps {
  /** Currently selected color (hex string) */
  value?: string | null;
  /** Change handler - returns hex string */
  onChange: (color: string) => void;
  /** Show custom hex input */
  showCustomInput?: boolean;
  /** Preset colors to display (defaults to brand + common colors) */
  presetColors?: string[];
  /** Number of columns in the grid */
  columns?: number;
  /** Size of color swatches */
  swatchSize?: number;
  /** Label text */
  label?: string;
  /** Helper text */
  helperText?: string;
  /** Custom className */
  className?: string;
  /** Storage key for saved swatches (enables save feature) */
  storageKey?: string;
  /** Maximum number of saved swatches */
  maxSavedSwatches?: number;
}

// ═══════════════════════════════════════════════════════════════════
// COLOR PRESETS
// ═══════════════════════════════════════════════════════════════════

// Brand colors (from design tokens)
const BRAND_COLORS = [
  '#1b3b5a', // Foundry Blue
  '#cba052', // Forge Gold
  '#4a7c59', // Timberline Green
];

// Family member colors - vibrant but not too saturated
const MEMBER_COLORS = [
  '#3b82f6', // Blue
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#eab308', // Yellow
  '#22c55e', // Green
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#6366f1', // Indigo
];

// Neutral colors
const NEUTRAL_COLORS = [
  '#64748b', // Slate
  '#78716c', // Stone
  '#737373', // Neutral
];

// All default preset colors
const DEFAULT_PRESET_COLORS = [
  ...BRAND_COLORS,
  ...MEMBER_COLORS,
  ...NEUTRAL_COLORS,
];

// ═══════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════

/**
 * Validate hex color string
 */
function isValidHex(hex: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
}

/**
 * Normalize hex color (ensure # prefix, uppercase)
 */
function normalizeHex(hex: string): string {
  let normalized = hex.trim();
  if (!normalized.startsWith('#')) {
    normalized = '#' + normalized;
  }
  return normalized.toLowerCase();
}

/**
 * Get contrasting text color (black or white) for a given background
 */
function getContrastColor(hex: string): string {
  // Remove # if present
  const color = hex.replace('#', '');

  // Parse RGB values
  const r = parseInt(color.substr(0, 2), 16);
  const g = parseInt(color.substr(2, 2), 16);
  const b = parseInt(color.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  return luminance > 0.5 ? '#000000' : '#ffffff';
}

// ═══════════════════════════════════════════════════════════════════
// COLOR PICKER COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const ColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  showCustomInput = true,
  presetColors = DEFAULT_PRESET_COLORS,
  columns = 8,
  swatchSize = 36,
  label,
  helperText,
  className,
  storageKey = 'colorpicker-saved-swatches',
  maxSavedSwatches = 10,
}) => {
  const [customHex, setCustomHex] = useState(value || '');
  const [inputError, setInputError] = useState<string | null>(null);
  const [savedSwatches, setSavedSwatches] = useState<string[]>([]);

  // Normalize the current value for comparison (defined first since others depend on it)
  const normalizedValue = useMemo(() => {
    if (!value) return null;
    return normalizeHex(value);
  }, [value]);

  // Load saved swatches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setSavedSwatches(parsed);
        }
      }
    } catch (err) {
      console.error('Failed to load saved swatches:', err);
    }
  }, [storageKey]);

  // Save swatches to localStorage
  const persistSwatches = useCallback((swatches: string[]) => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(swatches));
    } catch (err) {
      console.error('Failed to save swatches:', err);
    }
  }, [storageKey]);

  // Add current color to saved swatches
  const saveCurrentColor = useCallback(() => {
    if (!normalizedValue) return;

    // Don't add if already in presets or saved
    if (presetColors.some(c => normalizeHex(c) === normalizedValue)) return;
    if (savedSwatches.some(c => normalizeHex(c) === normalizedValue)) return;

    const newSwatches = [normalizedValue, ...savedSwatches].slice(0, maxSavedSwatches);
    setSavedSwatches(newSwatches);
    persistSwatches(newSwatches);
  }, [normalizedValue, presetColors, savedSwatches, maxSavedSwatches, persistSwatches]);

  // Remove a saved swatch
  const removeSavedSwatch = useCallback((color: string) => {
    const newSwatches = savedSwatches.filter(c => c !== color);
    setSavedSwatches(newSwatches);
    persistSwatches(newSwatches);
  }, [savedSwatches, persistSwatches]);

  // Check if current color can be saved
  const canSaveColor = useMemo(() => {
    if (!normalizedValue) return false;
    // Can't save if it's already a preset or already saved
    if (presetColors.some(c => normalizeHex(c) === normalizedValue)) return false;
    if (savedSwatches.some(c => normalizeHex(c) === normalizedValue)) return false;
    return true;
  }, [normalizedValue, presetColors, savedSwatches]);

  // Check if a color is selected
  const isSelected = useCallback((color: string) => {
    if (!normalizedValue) return false;
    return normalizeHex(color) === normalizedValue;
  }, [normalizedValue]);

  // Handle preset color selection
  const handlePresetClick = useCallback((color: string) => {
    onChange(color);
    setCustomHex(color);
    setInputError(null);
  }, [onChange]);

  // Handle custom hex input
  const handleCustomChange = useCallback((input: string) => {
    setCustomHex(input);
    setInputError(null);

    // Auto-add # if user types without it
    let hex = input.trim();
    if (hex && !hex.startsWith('#')) {
      hex = '#' + hex;
    }

    // Validate and apply if valid
    if (hex.length === 0) {
      return;
    }

    if (isValidHex(hex)) {
      onChange(normalizeHex(hex));
    } else if (hex.length >= 4) {
      setInputError('Invalid hex color');
    }
  }, [onChange]);

  // Handle input blur - validate final value
  const handleInputBlur = useCallback(() => {
    if (!customHex) return;

    let hex = customHex.trim();
    if (!hex.startsWith('#')) {
      hex = '#' + hex;
    }

    if (isValidHex(hex)) {
      const normalized = normalizeHex(hex);
      setCustomHex(normalized);
      onChange(normalized);
      setInputError(null);
    } else {
      setInputError('Invalid hex color');
    }
  }, [customHex, onChange]);

  return (
    <div className={className} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Label */}
      {label && (
        <label style={{
          display: 'block',
          fontFamily: 'var(--kff-data-font)',
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--ion-color-medium)',
        }}>
          {label}
        </label>
      )}

      {/* Current selection preview */}
      {normalizedValue && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: 12,
          background: 'var(--ion-color-step-50)',
          borderRadius: 8,
        }}>
          <div style={{
            width: 32,
            height: 32,
            borderRadius: 6,
            backgroundColor: normalizedValue,
            border: '2px solid var(--ion-border-color)',
            flexShrink: 0,
          }} />
          <div style={{ flex: 1 }}>
            <div style={{
              fontFamily: 'var(--kff-heading-font)',
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--ion-text-color)',
            }}>
              {normalizedValue.toUpperCase()}
            </div>
            <div style={{
              fontFamily: 'var(--kff-data-font)',
              fontSize: 12,
              color: 'var(--ion-color-medium)',
            }}>
              Selected color
            </div>
          </div>
        </div>
      )}

      {/* Color swatches grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
          gap: 6,
        }}
      >
        {presetColors.map((color) => {
          const selected = isSelected(color);
          const contrastColor = getContrastColor(color);

          return (
            <button
              key={color}
              type="button"
              onClick={() => handlePresetClick(color)}
              title={color}
              style={{
                width: swatchSize,
                height: swatchSize,
                borderRadius: 8,
                backgroundColor: color,
                border: selected
                  ? '3px solid var(--kff-forge-gold)'
                  : '2px solid transparent',
                cursor: 'pointer',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
                outline: 'none',
              }}
              aria-label={`Select color ${color}`}
              aria-pressed={selected}
            >
              {selected && (
                <IonIcon
                  icon={checkmark}
                  style={{
                    fontSize: 18,
                    color: contrastColor,
                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
                  }}
                />
              )}
            </button>
          );
        })}
      </div>

      {/* Saved swatches section */}
      {savedSwatches.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <div style={{
            fontFamily: 'var(--kff-data-font)',
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--ion-color-medium)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            marginBottom: 8,
          }}>
            Saved Colors
          </div>
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 6,
          }}>
            {savedSwatches.map((color) => {
              const selected = isSelected(color);
              const contrastColor = getContrastColor(color);

              return (
                <div key={color} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    onClick={() => handlePresetClick(color)}
                    title={color}
                    style={{
                      width: swatchSize,
                      height: swatchSize,
                      borderRadius: 8,
                      backgroundColor: color,
                      border: selected
                        ? '3px solid var(--kff-forge-gold)'
                        : '2px solid var(--ion-border-color)',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.15s ease',
                      outline: 'none',
                    }}
                    aria-label={`Select saved color ${color}`}
                    aria-pressed={selected}
                  >
                    {selected && (
                      <IonIcon
                        icon={checkmark}
                        style={{
                          fontSize: 18,
                          color: contrastColor,
                          filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.3))',
                        }}
                      />
                    )}
                  </button>
                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSavedSwatch(color);
                    }}
                    style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: 'var(--ion-color-danger)',
                      border: '2px solid var(--ion-background-color)',
                      cursor: 'pointer',
                      padding: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    aria-label={`Remove saved color ${color}`}
                  >
                    <IonIcon
                      icon={closeCircle}
                      style={{ fontSize: 14, color: 'white' }}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Custom color input - native color picker + hex display */}
      {showCustomInput && (
        <div style={{
          marginTop: 12,
          padding: 12,
          background: 'var(--ion-color-step-50)',
          borderRadius: 10,
          border: '1px solid var(--ion-border-color)',
        }}>
          <label style={{
            display: 'block',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--ion-color-medium)',
            marginBottom: 10,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}>
            Custom Color
          </label>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            {/* Native color picker with styled wrapper */}
            <div style={{
              position: 'relative',
              width: 52,
              height: 52,
              borderRadius: 10,
              overflow: 'hidden',
              border: '2px solid var(--ion-border-color)',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
              flexShrink: 0,
            }}>
              <input
                type="color"
                value={normalizedValue || '#3b82f6'}
                onChange={(e) => {
                  const color = e.target.value;
                  setCustomHex(color);
                  onChange(color);
                  setInputError(null);
                }}
                style={{
                  position: 'absolute',
                  top: -8,
                  left: -8,
                  width: 'calc(100% + 16px)',
                  height: 'calc(100% + 16px)',
                  padding: 0,
                  border: 'none',
                  cursor: 'pointer',
                  background: 'transparent',
                }}
                aria-label="Pick custom color"
              />
            </div>
            {/* Hex value display and input */}
            <div style={{ flex: 1 }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                background: 'var(--ion-background-color)',
                borderRadius: 8,
                border: inputError ? '1px solid var(--ion-color-danger)' : '1px solid var(--ion-border-color)',
              }}>
                <span style={{
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 11,
                  color: 'var(--ion-color-medium)',
                  textTransform: 'uppercase',
                }}>
                  HEX
                </span>
                <input
                  type="text"
                  value={customHex}
                  onChange={(e) => handleCustomChange(e.target.value)}
                  onBlur={handleInputBlur}
                  placeholder="#ff5733"
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontFamily: 'var(--kff-data-font)',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ion-text-color)',
                    outline: 'none',
                    textTransform: 'uppercase',
                  }}
                />
              </div>
              {inputError && (
                <div style={{
                  fontFamily: 'var(--kff-data-font)',
                  fontSize: 11,
                  color: 'var(--ion-color-danger)',
                  marginTop: 4,
                }}>
                  {inputError}
                </div>
              )}
            </div>
            {/* Save to swatches button */}
            <button
              type="button"
              onClick={saveCurrentColor}
              disabled={!canSaveColor}
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: canSaveColor ? 'var(--kff-forge-gold)' : 'var(--ion-color-step-100)',
                border: 'none',
                cursor: canSaveColor ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s ease',
                opacity: canSaveColor ? 1 : 0.5,
                flexShrink: 0,
              }}
              title={canSaveColor ? 'Save to swatches' : 'Color already saved or is a preset'}
              aria-label="Save color to swatches"
            >
              <IonIcon
                icon={addOutline}
                style={{
                  fontSize: 20,
                  color: canSaveColor ? 'white' : 'var(--ion-color-medium)',
                }}
              />
            </button>
          </div>
        </div>
      )}

      {/* Helper text */}
      {helperText && !inputError && (
        <div style={{
          fontFamily: 'var(--kff-data-font)',
          fontSize: 12,
          color: 'var(--ion-color-medium)',
        }}>
          {helperText}
        </div>
      )}
    </div>
  );
};

ColorPicker.displayName = 'ColorPicker';

// ═══════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════

export { DEFAULT_PRESET_COLORS, BRAND_COLORS, MEMBER_COLORS, NEUTRAL_COLORS };
export { isValidHex, normalizeHex, getContrastColor };

export default ColorPicker;
